import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";

import { useEffect, useState, useRef } from "react";

import Loader from "react-loader-spinner";
import { useParams } from "react-router";
import { Prompt, useHistory } from "react-router-dom";

import BeforeUnLoad from "../../components/BeforeUnLoad";
import CustomLoader from "../../components/CustomLoader";
import CasesValidateSchema from "../../components/CaseView/CasesValidateSchema";
import Files from "./Files";
import Invoice from "./Invoice";

import { CaseDataResponse, TabType, Subject } from "../../types/CaseTypes";
import { useCaseWebSocket } from "../../hooks/CaseView/useCaseWebSocket";
import CollaborationNotifications from "../../components/CaseView/CollaborationNotificationsProps";
import CaseTopBar from "../../components/CaseView/CaseTopBar";
import { useCaseApi } from "../../hooks/CaseView/useCaseApi";
import { useCaseViewStyle } from "../../components/CaseView/useCaseViewStyle";
import { useStore } from "../../context/StoreContext";
import CaseDetailsTab from "./CaseDetailsView";
import TasksTab from "./TasksTab";
import {
  DeleteButton,
  PrimaryButton,
  StyledTabButton,
} from "../../components/shared/helpers/GlobalStylesv5";
import { tokens } from "../../theme/tokens";

type RouteParams = {
  id: string;
  type?: TabType;
};

function Case() {
  const { id, type } = useParams<RouteParams>();

  const { uploadLoading, data } = useStore();

  const [tabState, setTabState] = useState<TabType>(type || "case");
  const formRef = useRef<FormikProps<CaseDataResponse>>(null);
  const { fetchCase, saveCase, loaderState, open } = useCaseApi(id);
  const { userInCaseOpen, handleClose } = useCaseWebSocket(id);

  const [nextLocation, setNextLocation] = useState<string | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const allowTransitionRef = useRef(false);

  const handleBlockedNavigation = (location: any) => {
    // 1. If we are staying in the cases route, let it pass
    if (location.pathname.startsWith("/app/cases/")) {
      return true;
    }

    // 2. If we have explicitly allowed the transition (clicked Save/Discard), let it pass
    if (allowTransitionRef.current) {
      return true;
    }

    // 3. Otherwise, block and show modal
    setNextLocation(location.pathname);
    setShowExitModal(true);
    return false;
  };

  const classes = useCaseViewStyle();
  const history = useHistory();

  const handleTabStateChange = (value: TabType) => {
    history.push(`/app/cases/${id}/${value}`);
    setTabState(value);
  };

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const handleSubmit = (values: CaseDataResponse) => {
    saveCase(values);
  };

  if (loaderState) {
    return (
      <div style={{ width: "100%", height: "100vh", position: "relative" }}>
        <div
          style={{
            margin: "0",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: "50%",
          }}
        >
          <Loader
            type="Puff"
            color={tokens.color.brand.primary}
            height={100}
            width={100}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: tokens.color.surface.sunken,
      }}
    >
      <Formik
        enableReinitialize
        initialValues={data}
        validationSchema={CasesValidateSchema}
        innerRef={formRef}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({ isSubmitting, dirty, values, setFieldTouched, setFieldValue }) => {
          const isDirty = dirty || uploadLoading.isActive;

          const handleCreateSubject = async (subject: Subject) => {
            const newSubjects = [
              ...(values.case_detail.case_details.Subjects_information ?? []),
              subject,
            ];
            setFieldValue('case_detail.case_details.Subjects_information', newSubjects);
            const updated: CaseDataResponse = {
              ...values,
              case_detail: {
                ...values.case_detail,
                case_details: {
                  ...values.case_detail.case_details,
                  Subjects_information: newSubjects,
                },
              },
            };
            await saveCase(updated);
          };

          const renderTabContent = (values: CaseDataResponse) => {
            switch (tabState) {
              case "case":
                return <CaseDetailsTab />;
              case "docs":
                return (
                  <>
                    {data.case_detail.file_no && (
                      <Files case_id={data.case_detail.file_no} />
                    )}
                  </>
                );
              case "invoice":
                return <Invoice caseData={data} />;
              case "tasks":
                return (
                  <TasksTab
                    caseId={data.case_detail.file_no ?? id}
                    clientId={data.case_detail.client_id}
                    subjects={values.case_detail.case_details?.Subjects_information ?? []}
                    onCreateSubject={handleCreateSubject}
                  />
                );
              default:
                return null;
            }
          };
          return (
            <Form>
              {/* Collaboration Alert */}
              <CollaborationNotifications
                userInCaseOpen={userInCaseOpen}
                handleClose={handleClose}
              />

              {/* Prompt for unsaved changes */}
              {/* <Prompt
                when={dirty || uploadLoading.isActive}
                message={(location: { pathname: string }) =>
                  location.pathname.startsWith("/app/cases/")
                    ? true
                    : "You have unsaved changes, are you sure you want to continue?"
                }
              /> */}
              <Prompt when={isDirty} message={handleBlockedNavigation} />

              {/* 2. Custom Exit Dialog — J12 dialog shell */}
              <Dialog
                open={showExitModal}
                onClose={() => setShowExitModal(false)}
              >
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                  You have unsaved changes. Would you like to save them before
                  leaving?
                </DialogContent>
                <DialogActions>
                  <StyledTabButton onClick={() => setShowExitModal(false)}>
                    Cancel
                  </StyledTabButton>

                  <DeleteButton
                    onClick={() => {
                      allowTransitionRef.current = true; // Set the bypass flag
                      setShowExitModal(false);
                      if (nextLocation) history.push(nextLocation);
                    }}
                  >
                    Discard & Leave
                  </DeleteButton>

                  <PrimaryButton
                    onClick={async () => {
                      await handleSubmit(values);
                      allowTransitionRef.current = true; // Set the bypass flag
                      setShowExitModal(false);
                      if (nextLocation) history.push(nextLocation);
                    }}
                  >
                    Save & Leave
                  </PrimaryButton>
                </DialogActions>
              </Dialog>

              <BeforeUnLoad state={dirty || uploadLoading.isActive} />

              {/* Top Navigation Bar */}
              <CaseTopBar
                caseId={id}
                userInCaseOpen={userInCaseOpen}
                tabState={tabState}
                onTabChange={handleTabStateChange}
                isDirty={isDirty}
                isSubmitting={isSubmitting}
                loaderState={loaderState}
                caseStatus={values.case_detail?.status}
              />

              {/* Tab Content */}
              {values.case_detail && renderTabContent(values)}

            </Form>
          );
        }}
      </Formik>

      <Dialog open={open}>{open && <CustomLoader />}</Dialog>
    </div>
  );
}

export default Case;
